/**
 * plateRow — is a gallery photo row a DATA PLATE? ONE rule for every reader: the server (confirm, proof read, mark, set
 * component) and the client (the gallery "Data plate" tab and its count, the plate controls). Moved here from
 * functions/src/fieldService/gallery/plateRow.ts (ISS-652): the client had its own copy, and the gallery tab used neither, so a
 * unit with two plates showed "Data plate (0)".
 *
 *   1. A person's category decides: data-plate = yes, any other category = no.
 *   2. Otherwise the AI's category: data-plate = yes.
 *   3. Otherwise a CAPTURE label from a plate field (the report put the photo in the component grid or the unit plate field):
 *      yes, unless a CONFIDENT classifier (>= 0.9) called it something else. Measured 2026-10-09: 59 of 59 recent plate-field photos
 *      were plates (Gemini shadow run), so the field is a reliable plate signal; it is still only a hint, never a proof.
 * Pure: no I/O. Never throws on a malformed row.
 */

export const PLATE_ROW_CATEGORY = 'data-plate';
/** The plate fields a capture label can come from (captureLabel.ts). */
const PLATE_VIAS: ReadonlySet<string> = new Set(['component-grid-row', 'unit-plate-field']);
const CONFIDENT_NON_PLATE = 0.9;

type Row = Record<string, unknown>;

const isRecord = (v: unknown): v is Row => typeof v === 'object' && v !== null && !Array.isArray(v);

export type PlateFieldVia = 'component-grid-row' | 'unit-plate-field';

/**
 * The plate-field evidence a capture label rests on ({ fieldKey, via }), or null. A PERSON's relabel of a capture-labelled photo
 * carries it forward (setComponentService), so correcting a wrong capture label does not turn the photo into a non-plate.
 */
export function plateFieldEvidence(row: unknown): { fieldKey: string; via: PlateFieldVia } | null {
  if (!isRecord(row) || (row.componentSource !== 'capture' && row.componentSource !== 'person')) return null;
  const e = isRecord(row.componentEvidence) ? row.componentEvidence : null;
  if (typeof e?.fieldKey !== 'string' || typeof e.via !== 'string' || !PLATE_VIAS.has(e.via)) return null;
  return { fieldKey: e.fieldKey, via: e.via as PlateFieldVia };
}

export function isPlateRow(row: unknown): boolean {
  if (!isRecord(row)) return false;
  if (typeof row.category === 'string') return row.category === PLATE_ROW_CATEGORY;
  const ai = isRecord(row.ai) ? row.ai : null;
  if (ai?.category === PLATE_ROW_CATEGORY) return true;
  if (!plateFieldEvidence(row)) return false;
  const confidentNonPlate = typeof ai?.category === 'string' && typeof ai.confidence === 'number' && ai.confidence >= CONFIDENT_NON_PLATE;
  return !confidentNonPlate;
}
